import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  CircularProgress,
  useTheme
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar
} from 'recharts';
import { getAdminMetrics } from '../api';


const StatCard = ({ title, value, trend, color = '#1976d2' }) => {
  const theme = useTheme();
  
  return (
    <Card sx={{ 
      background: `linear-gradient(135deg, ${color}15, ${color}05)`,
      border: `1px solid ${color}30`,
      borderRadius: 2,
      height: '100%',
      display: 'flex',
      flexDirection: 'column'
    }}>
      <CardContent sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="h6" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="h3" sx={{ color, fontWeight: 'bold', mb: 1 }}>
          {value?.toLocaleString() || 0}
        </Typography>
        {trend && (
          <Typography 
            variant="body2" 
            sx={{ 
              color: trend > 0 ? 'success.main' : trend < 0 ? 'error.main' : 'text.secondary',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5
            }}
          >
            {trend > 0 ? '↗' : trend < 0 ? '↘' : '→'} {Math.abs(trend)}% vs last period
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const MiniChart = ({ data, title, color = '#1976d2', type = 'line' }) => {
  const theme = useTheme();
  
  if (!data || data.length === 0) {
    return (
      <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography color="text.secondary">No data available</Typography>
      </Box>
    );
  }

  const ChartComponent = type === 'bar' ? BarChart : LineChart;
  const DataComponent = type === 'bar' ? Bar : Line;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, color: theme.palette.text.primary }}>
          {title}
        </Typography>
        <Box sx={{ flex: 1, minHeight: 200, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ChartComponent data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
              <XAxis 
                dataKey="date" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => {
                  const date = new Date(value);
                  return `${date.getMonth() + 1}/${date.getDate()}`;
                }}
              />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip 
                labelFormatter={(value) => new Date(value).toLocaleDateString()}
                formatter={(value) => [value, 'Count']}
              />
              <DataComponent 
                type="monotone" 
                dataKey="count" 
                stroke={color} 
                strokeWidth={2}
                fill={type === 'bar' ? color : undefined}
                fillOpacity={type === 'bar' ? 0.6 : undefined}
              />
            </ChartComponent>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

export default function AdminStatistics({ onLogout }) {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timeRange, setTimeRange] = useState(30);
  const theme = useTheme();

  const loadMetrics = useCallback(async (days = timeRange) => {
    setLoading(true);
    setError('');
    try {
      const data = await getAdminMetrics(days);
      setMetrics(data);
    } catch (err) {
      setError('Failed to load metrics');
      console.error('Metrics error:', err);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>
        <Button variant="contained" onClick={() => loadMetrics()}>
          Retry
        </Button>
      </Box>
    );
  }

  if (!metrics) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">No metrics data available</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ mb: 2, color: theme.palette.text.primary }}>
          Analytics Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary }}>
            Time Range:
          </Typography>
          {[7, 30, 90].map(days => (
            <Button
              key={days}
              variant={timeRange === days ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setTimeRange(days)}
              sx={{ minWidth: 60 }}
            >
              {days} days
            </Button>
          ))}
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Users" 
            value={metrics.totals?.users} 
            color="#1976d2"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Teams" 
            value={metrics.totals?.teams} 
            color="#388e3c"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Tests" 
            value={metrics.totals?.tests} 
            color="#f57c00"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard 
            title="Total Races" 
            value={metrics.totals?.races} 
            color="#d32f2f"
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
          <MiniChart 
            data={metrics.daily?.users} 
            title="User Registrations" 
            color="#1976d2"
            type="line"
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
          <MiniChart 
            data={metrics.daily?.teams} 
            title="Team Creations" 
            color="#388e3c"
            type="line"
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
          <MiniChart 
            data={metrics.daily?.tests} 
            title="Test Entries" 
            color="#f57c00"
            type="bar"
          />
        </Grid>
        <Grid item xs={12} md={6} sx={{ minWidth: 0 }}>
          <MiniChart 
            data={metrics.daily?.races} 
            title="Race Entries" 
            color="#d32f2f"
            type="bar"
          />
        </Grid>
      </Grid>

      {/* Overview Chart */}
      <Box sx={{ mt: 4 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Activity Overview
            </Typography>
            <Box sx={{ height: 300 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={metrics.daily?.users || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis tick={{ fontSize: 12 }} />
                  <Tooltip 
                    labelFormatter={(value) => new Date(value).toLocaleDateString()}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="count" 
                    stroke="#1976d2" 
                    strokeWidth={2}
                    name="Users"
                  />
                </LineChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Logout Button */}
      {onLogout && (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Button 
            variant="outlined" 
            color="primary" 
            onClick={onLogout}
            sx={{ 
              borderColor: 'rgba(255,255,255,0.15)', 
              color: theme.palette.text.primary, 
              '&:hover': { 
                borderColor: theme.palette.primary.main, 
                background: 'rgba(99,102,241,0.08)' 
              } 
            }}
          >
            Logout
          </Button>
        </Box>
      )}
    </Box>
  );
}
